import * as actionTypes from '../actions/actionTypes';
import { updateObject, shallowCompare } from '../../utils/utils';
import AuthUser from '../../models/users/AuthUser';

const initialState = {
  authUser: null,
  error: null,
  loading: false,
};

const createUserStart = (state) => {
  return updateObject(state, { loading: true });
};

const createUserSuccess = (state) => {
  return updateObject(state, { loading: false });
};

const createUserFail = (state, action) => {
  return updateObject(state, { error: action.error.message, loading: false });
};

const signInWithEmailStart = (state) => {
  return updateObject(state, { loading: true });
};

const signInWithEmailSuccess = (state, action) => {
  const authUser = action.payload.authUser;
  return updateObject(state, { loading: false, authUser });
};

const signInWithEmailFail = (state, action) => {
  return updateObject(state, { error: action.error.message, loading: false });
};

const signInWithTokenStart = (state) => {
  return updateObject(state, { loading: true });
};

const signInWithTokenSuccess = (state, action) => {
  const authUser = action.payload.authUser;
  return updateObject(state, { loading: false, authUser });
};

const signInWithTokenFail = (state, action) => {
  return updateObject(state, { error: action.payload.error.message, loading: false });
};

const signInWithGoogleStart = (state) => {
  return updateObject(state, { loading: true });
};

const signInWithGoogleSuccess = (state) => {
  return updateObject(state, { loading: false, authUser: null });
};

const signInWithGoogleFail = (state, action) => {
  return updateObject(state, { error: action.error.message, loading: false });
};

const signInWithFacebookStart = (state) => {
  return updateObject(state, { loading: true });
};

const signInWithFacebookSuccess = (state) => {
  return updateObject(state, { loading: false, authUser: null });
};

const signInWithFacebookFail = (state, action) => {
  return updateObject(state, { error: action.error.message, loading: false });
};

const passwordResetStart = (state) => {
  return updateObject(state, { loading: true });
};

const passwordResetSuccess = (state) => {
  return updateObject(state, { loading: false });
};

const passwordResetFail = (state, action) => {
  return updateObject(state, { error: action.error.message, loading: false });
};

const updatePasswordStart = (state) => {
  return updateObject(state, { loading: true });
};

const updatePasswordSuccess = (state) => {
  return updateObject(state, { loading: false });
};

const updatePasswordFail = (state, action) => {
  return updateObject(state, { error: action.error.message, loading: false });
};

const setPasswordStart = (state) => {
  return updateObject(state, { loading: true });
};

const setPasswordSuccess = (state) => {
  return updateObject(state, { loading: false });
};

const setPasswordFail = (state, action) => {
  return updateObject(state, { error: action.error.message, loading: false });
};

const authStateChangedStart = (state) => {
  return updateObject(state, { loading: true });
};

const authStateChangedSuccess = (state, action) => {
  const authUser = action.payload.authUser;
  if (state.authUser && shallowCompare(state.authUser, authUser)) {
    return updateObject(state, { loading: false });
  }
  return updateObject(state, { authUser, loading: false });
};

const authStateChangedFail = (state, action) => {
  return updateObject(state, {
    error: action.error.message,
    authUser: null,
    loading: false,
  });
};

const logoutStart = (state) => {
  return updateObject(state, { loading: true });
};

const logoutSuccess = (state) => {
  return updateObject(state, { loading: false });
};

const logoutFail = (state, action) => {
  return updateObject(state, { error: action.error.message, loading: false });
};

const setNamesStart = (state) => {
  return updateObject(state, {});
};

const setNamesSuccess = (state, action) => {
  const names = action.payload.names || {};
  return updateObject(state, { authUser: new AuthUser({ ...state.authUser, ...names }) });
};

const setNamesFail = (state, action) => {
  return updateObject(state, { error: action.payload.error.message });
};

const setLanguageStart = (state) => {
  return updateObject(state, { loading: true });
};

const setLanguageSuccess = (state) => {
  return updateObject(state, { loading: false });
};

const setLanguageFail = (state, action) => {
  return updateObject(state, { error: action.payload.error.message, loading: false });
};

const setDarkModeStart = (state) => {
  return updateObject(state, {});
};

const setDarkModeSuccess = (state) => {
  return updateObject(state, {});
};

const setDarkModeFail = (state, action) => {
  return updateObject(state, { error: action.payload.error.message });
};

const setDisableTasksDoneStart = (state) => {
  return updateObject(state, {});
};

const setDisableTasksDoneSuccess = (state, action) => {
  const disableTasksDone = action.payload.disableTasksDone;
  return updateObject(state, { authUser: new AuthUser({ ...state.authUser, disableTasksDone }) });
};

const setDisableTasksDoneFail = (state, action) => {
  return updateObject(state, { error: action.payload.error.message });
};

const setDisableAdviceShowStart = (state) => {
  return updateObject(state, {});
};

const setDisableAdviceShowSuccess = (state, action) => {
  const disableAdviceShow = action.payload.disableAdviceShow;
  return updateObject(state, { authUser: new AuthUser({ ...state.authUser, disableAdviceShow }) });
};

const setDisableAdviceShowFail = (state, action) => {
  return updateObject(state, { error: action.payload.error.message });
};

const setAdviceShowedAtStart = (state) => {
  return updateObject(state, {});
};

const setAdviceShowedAtSuccess = (state, action) => {
  const adviceShowedAt = action.payload.adviceShowedAt;
  return updateObject(state, { authUser: new AuthUser({ ...state.authUser, adviceShowedAt }) });
};

const setAdviceShowedAtFail = (state, action) => {
  return updateObject(state, { error: action.payload.error.message });
};

const setShowGlobalTasksAsListStart = (state) => {
  return updateObject(state, {});
};

const setShowGlobalTasksAsListSuccess = (state, action) => {
  const showGlobalTasksAsList = action.payload.showGlobalTasksAsList;
  return updateObject(state, { authUser: new AuthUser({ ...state.authUser, showGlobalTasksAsList }) });
};

const setShowGlobalTasksAsListFail = (state, action) => {
  return updateObject(state, { error: action.payload.error.message });
};

const reducer = (state = initialState, action) => {
  switch (action.type) {
    case actionTypes.CREATE_USER_START:
      return createUserStart(state, action);
    case actionTypes.CREATE_USER_SUCCESS:
      return createUserSuccess(state, action);
    case actionTypes.CREATE_USER_FAIL:
      return createUserFail(state, action);
    case actionTypes.SIGN_IN_WITH_EMAIL_START:
      return signInWithEmailStart(state, action);
    case actionTypes.SIGN_IN_WITH_EMAIL_SUCCESS:
      return signInWithEmailSuccess(state, action);
    case actionTypes.SIGN_IN_WITH_EMAIL_FAIL:
      return signInWithEmailFail(state, action);
    case actionTypes.SIGN_IN_WITH_TOKEN_START:
      return signInWithTokenStart(state, action);
    case actionTypes.SIGN_IN_WITH_TOKEN_SUCCESS:
      return signInWithTokenSuccess(state, action);
    case actionTypes.SIGN_IN_WITH_TOKEN_FAIL:
      return signInWithTokenFail(state, action);
    case actionTypes.SIGN_IN_WITH_GOOGLE_START:
      return signInWithGoogleStart(state, action);
    case actionTypes.SIGN_IN_WITH_GOOGLE_SUCCESS:
      return signInWithGoogleSuccess(state, action);
    case actionTypes.SIGN_IN_WITH_GOOGLE_FAIL:
      return signInWithGoogleFail(state, action);
    case actionTypes.SIGN_IN_WITH_FACEBOOK_START:
      return signInWithFacebookStart(state, action);
    case actionTypes.SIGN_IN_WITH_FACEBOOK_SUCCESS:
      return signInWithFacebookSuccess(state, action);
    case actionTypes.SIGN_IN_WITH_FACEBOOK_FAIL:
      return signInWithFacebookFail(state, action);
    case actionTypes.PASSWORD_RESET_START:
      return passwordResetStart(state, action);
    case actionTypes.PASSWORD_RESET_SUCCESS:
      return passwordResetSuccess(state, action);
    case actionTypes.PASSWORD_RESET_FAIL:
      return passwordResetFail(state, action);
    case actionTypes.UPDATE_PASSWORD_START:
      return updatePasswordStart(state, action);
    case actionTypes.UPDATE_PASSWORD_SUCCESS:
      return updatePasswordSuccess(state, action);
    case actionTypes.UPDATE_PASSWORD_FAIL:
      return updatePasswordFail(state, action);
    case actionTypes.SET_PASSWORD_START:
      return setPasswordStart(state, action);
    case actionTypes.SET_PASSWORD_SUCCESS:
      return setPasswordSuccess(state, action);
    case actionTypes.SET_PASSWORD_FAIL:
      return setPasswordFail(state, action);
    case actionTypes.AUTH_STATE_CHANGED_START:
      return authStateChangedStart(state, action);
    case actionTypes.AUTH_STATE_CHANGED_SUCCESS:
      return authStateChangedSuccess(state, action);
    case actionTypes.AUTH_STATE_CHANGED_FAIL:
      return authStateChangedFail(state, action);
    case actionTypes.LOGOUT_START:
      return logoutStart(state, action);
    case actionTypes.LOGOUT_SUCCESS:
      return logoutSuccess(state, action);
    case actionTypes.LOGOUT_FAIL:
      return logoutFail(state, action);
    case actionTypes.SET_NAMES_START:
      return setNamesStart(state, action);
    case actionTypes.SET_NAMES_SUCCESS:
      return setNamesSuccess(state, action);
    case actionTypes.SET_NAMES_FAIL:
      return setNamesFail(state, action);
    case actionTypes.SET_LANGUAGE_START:
      return setLanguageStart(state, action);
    case actionTypes.SET_LANGUAGE_SUCCESS:
      return setLanguageSuccess(state, action);
    case actionTypes.SET_LANGUAGE_FAIL:
      return setLanguageFail(state, action);
    case actionTypes.SET_DARK_MODE_START:
      return setDarkModeStart(state, action);
    case actionTypes.SET_DARK_MODE_SUCCESS:
      return setDarkModeSuccess(state, action);
    case actionTypes.SET_DARK_MODE_FAIL:
      return setDarkModeFail(state, action);
    case actionTypes.SET_DISABLE_TASKS_DONE_START:
      return setDisableTasksDoneStart(state, action);
    case actionTypes.SET_DISABLE_TASKS_DONE_SUCCESS:
      return setDisableTasksDoneSuccess(state, action);
    case actionTypes.SET_DISABLE_TASKS_DONE_FAIL:
      return setDisableTasksDoneFail(state, action);
    case actionTypes.SET_DISABLE_ADVICE_SHOW_START:
      return setDisableAdviceShowStart(state, action);
    case actionTypes.SET_DISABLE_ADVICE_SHOW_SUCCESS:
      return setDisableAdviceShowSuccess(state, action);
    case actionTypes.SET_DISABLE_ADVICE_SHOW_FAIL:
      return setDisableAdviceShowFail(state, action);
    case actionTypes.SET_ADVICE_SHOWED_AT_START:
      return setAdviceShowedAtStart(state, action);
    case actionTypes.SET_ADVICE_SHOWED_AT_SUCCESS:
      return setAdviceShowedAtSuccess(state, action);
    case actionTypes.SET_ADVICE_SHOWED_AT_FAIL:
      return setAdviceShowedAtFail(state, action);
    case actionTypes.SET_SHOW_GLOBAL_TASKS_AS_LIST_START:
      return setShowGlobalTasksAsListStart(state, action);
    case actionTypes.SET_SHOW_GLOBAL_TASKS_AS_LIST_SUCCESS:
      return setShowGlobalTasksAsListSuccess(state, action);
    case actionTypes.SET_SHOW_GLOBAL_TASKS_AS_LIST_FAIL:
      return setShowGlobalTasksAsListFail(state, action);

    default:
      return state;
  }
};

export default reducer;
